import React, { useState } from "react";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import "./QuoteModal.css";

// Helper function to encode form data for Netlify
const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    project_brief: "",
    botField: "", // Changed for Netlify compatibility
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Submit to Netlify
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "quote-request", ...formData }),
    })
      .then(() => {
        toast.success("Quote request sent! We will review your brief and contact you shortly.");
        setFormData({ name: "", email: "", phone: "", service: "", budget: "", project_brief: "", botField: "" });
        onClose();
      })
      .catch((error) => {
        console.error("Quote submission error:", error);
        toast.error("Something went wrong. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          <X size={24} />
        </button>
        
        <div className="modal-header">
          <h2 className="modal-title" id="quote-modal-title">Get a Quote</h2>
          <p className="modal-subtitle">
            Tell us about your project and we'll send you a detailed quote.
          </p>
        </div>

        {/* Netlify specific form attributes added here */}
        <form 
          className="quote-form" 
          onSubmit={handleSubmit}
          name="quote-request"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="botField"
        >
          <input type="hidden" name="form-name" value="quote-request" />
          
          <p className="hidden" style={{ display: 'none' }}>
            <label>Don’t fill this out: <input name="botField" onChange={handleChange} /></label>
          </p>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="quote-name" className="form-label">Full Name *</label>
              <input type="text" id="quote-name" name="name" value={formData.name} onChange={handleChange} required className="form-input" placeholder="John Doe" />
            </div>
            <div className="form-group">
              <label htmlFor="quote-email" className="form-label">Email Address *</label>
              <input type="email" id="quote-email" name="email" value={formData.email} onChange={handleChange} required className="form-input" placeholder="your@email.com" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="quote-phone" className="form-label">Phone Number *</label>
              <input type="tel" id="quote-phone" name="phone" value={formData.phone} onChange={handleChange} required className="form-input" placeholder="+92 (333) 000-0000" />
            </div>
            <div className="form-group">
              <label htmlFor="quote-service" className="form-label">Service *</label>
              <select id="quote-service" name="service" value={formData.service} onChange={handleChange} required className="form-input">
                <option value="">Select a service</option>
                <option value="Single Ad">Single Ad</option>
                <option value="Social Media Services">Social Media Services</option>
                <option value="DVC, TVC or SF">DVC, TVC, SF</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="quote-budget" className="form-label">Budget Range</label>
            <select id="quote-budget" name="budget" value={formData.budget} onChange={handleChange} className="form-input">
              <option value="">Select budget range</option>
              <option value="Under 30kPKR">Under 30kPKR</option>
              <option value="30k - 50k">30k - 50k</option>
              <option value="50k - 60k">50k - 60k</option>
              <option value="70k - 100k">70k - 100k</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="quote-brief" className="form-label">Project Brief *</label>
            <textarea id="quote-brief" name="project_brief" value={formData.project_brief} onChange={handleChange} required rows="5" className="form-textarea" placeholder="Describe your project, goals, and timeline..." />
          </div>

          <button type="submit" className="btn-submit-quote" disabled={loading}>
            {loading ? "Sending..." : "Request Quote"}
            {!loading && <Send size={18} />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuoteModal;